import { ReactNode } from "react";
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from "lucide-react";
import { Column } from "@tanstack/react-table";
import { Button } from "@/components/Base/Button";
import { Tooltip } from "@/components/Common/Tooltip";
import { cn } from "@/lib/utils";

type HeaderSortableProps<T> = {
  column: Column<T, unknown>;
  title: string;
  labelClassName?: string;
  /**
   * Optional explainer shown on hover/focus of the header. Use it where the
   * column label alone does not say what the figure means or how it is
   * derived; omit it and the header renders exactly as before.
   */
  tooltip?: ReactNode;
};

export const HeaderSortable = <T,>({
  column,
  title,
  labelClassName,
  tooltip,
}: HeaderSortableProps<T>) => {
  const header = (
    <Button
      variant="ghost-alt"
      className={cn(
        "h-auto p-0 text-primary-foreground hover:bg-transparent hover:text-primary-foreground",
        labelClassName
      )}
      onClick={() => {
        if (column.getIsSorted() === "asc") {
          column.toggleSorting(true, false);
        } else if (column.getIsSorted() === "desc") {
          column.clearSorting();
        } else {
          column.toggleSorting(false, false);
        }
      }}
    >
      <span className="mr-2">{title}</span>
      {column.getIsSorted() === "desc" ? (
        <ArrowDownIcon size={14} />
      ) : column.getIsSorted() === "asc" ? (
        <ArrowUpIcon size={14} />
      ) : (
        <ArrowUpDownIcon size={14} />
      )}
    </Button>
  );

  if (!tooltip) return header;

  // 200ms rather than the shared 700ms default: this is a "what is this
  // column?" explainer, so it has to arrive while the pointer is still on
  // the header. max-w-xs keeps a full sentence from rendering as one long
  // line, and normal-case undoes the uppercase header styling callers pass
  // via labelClassName -- the tooltip portals out, but the copy is prose.
  return (
    <Tooltip
      message={tooltip}
      delayDuration={200}
      className="max-w-xs whitespace-normal text-xs font-normal normal-case leading-snug tracking-normal"
    >
      {header}
    </Tooltip>
  );
};
